// MinAvailableRam.jsx
import { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { MdAnalytics, MdMemory } from "react-icons/md";
import { PerformanceService } from "../../../../application/services/performanceService";

const performanceService = new PerformanceService();

const COLORS = [
  "#00C2FF",
  "#FFB800",
  "#FF6B6B",
  "#51CF66",
  "#FF8787",
  "#845EF7",
  "#FCC419",
  "#20C997",
];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-primary-dark border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg max-w-[250px] sm:max-w-none">
        <p className="text-white text-xs sm:text-sm font-medium mb-2">{label}</p>
        {payload.map((entry, index) => {
          const timestampKey = `${entry.name}-timestamp`;
          const timestamp = entry.payload[timestampKey];
          const percentage = entry.value;
          const availableGB = ((8 * percentage) / 100).toFixed(2);
          return (
            <p key={index} className="text-gray-300 text-[10px] sm:text-xs mb-1">
              <span style={{ color: entry.color }}>●</span> {entry.name}: {percentage}% ({availableGB} GB available)
              {timestamp && <span className="text-gray-500 ml-1 sm:ml-2">at {timestamp}</span>}
            </p>
          );
        })}
      </div>
    );
  }
  return null;
};

const MinAvailableRam = ({ vmIds, startDate, endDate }) => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [vmNames, setVmNames] = useState([]);

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    if (dateStr.includes("-")) {
      const [year, month, day] = dateStr.split("-");
      return `${day}/${month}`;
    }
    if (dateStr.includes("/")) {
      const [day, month, year] = dateStr.split("/");
      return `${day}/${month}`;
    }
    return dateStr;
  };

  const extractTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    const parts = dateTimeStr.split("T");
    if (parts.length < 2) return "";
    const timePart = parts[1];
    return timePart.substring(0, 8);
  };

  useEffect(() => {
    if (startDate && endDate && vmIds && vmIds.length > 0) {
      fetchAvailableRamData();
    }
  }, [startDate, endDate, vmIds]);

  const fetchAvailableRamData = async () => {
    setLoading(true);
    setError(null);
    try {
      const allVmData = [];
      const names = [];

      for (const vmId of vmIds) {
        const result = await performanceService.getVmAvailableRam(
          vmId,
          startDate,
          endDate,
        );
        if (result.success && result.data) {
          allVmData.push({
            vmId: result.data.vmId,
            vmName: result.data.vmName,
            dailyAvailableRam: result.data.dailyAvailableRam,
          });
          names.push(result.data.vmName);
        }
      }
      setVmNames(names);

      if (allVmData.length === 0) {
        setChartData([]);
        setError("No available RAM data available");
        return;
      }

      const allDates = new Set();
      allVmData.forEach((vm) => {
        vm.dailyAvailableRam.forEach((day) => {
          allDates.add(day.date);
        });
      });

      const sortedDates = Array.from(allDates).sort();

      const transformedData = sortedDates.map((date) => {
        const dataPoint = { date: formatDisplayDate(date) };
        allVmData.forEach((vm) => {
          const dayData = vm.dailyAvailableRam.find((day) => day.date === date);
          if (
            dayData &&
            dayData.availableRamPercentage !== null &&
            dayData.availableRamPercentage !== undefined
          ) {
            dataPoint[vm.vmName] = dayData.availableRamPercentage;
            dataPoint[`${vm.vmName}-timestamp`] = extractTime(
              dayData.timestamp,
            );
          } else {
            dataPoint[vm.vmName] = null;
            dataPoint[`${vm.vmName}-timestamp`] = "";
          }
        });
        return dataPoint;
      });

      setChartData(transformedData);
    } catch (err) {
      console.error("Error fetching available RAM data:", err);
      setError("Failed to load available RAM performance data");
    } finally {
      setLoading(false);
    }
  };

  if (!vmIds || vmIds.length === 0) {
    return (
      <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4">
        <div className="text-center py-6 sm:py-8">
          <p className="text-gray-400 text-sm sm:text-base">No VM selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-dark rounded-2xl p-4 sm:p-6 mx-2 sm:m-4 overflow-x-hidden">
      <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
        <MdMemory className="text-primary-cyan text-xl sm:text-2xl" />
        <h3 className="text-lg sm:text-xl font-semibold text-white">Min Available RAM Values</h3>
      </div>

      <div className="mt-4 sm:mt-6">
        {loading ? (
          <div className="text-center py-6 sm:py-8">
            <div className="inline-block w-6 h-6 sm:w-8 sm:h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-3 text-sm sm:text-base">Loading available RAM data...</p>
          </div>
        ) : error ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-red-400 text-sm sm:text-base">{error}</p>
          </div>
        ) : chartData.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No available RAM data available for selected period</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[400px]" style={{ width: "100%", height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9CA3AF" 
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    interval={0}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis 
                    stroke="#9CA3AF" 
                    tick={{ fill: "#9CA3AF", fontSize: 10 }} 
                    unit="%" 
                    domain={[0, 100]} 
                    width={40}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "10px" }} />
                  {vmNames.map((vmName, index) => (
                    <Line
                      key={vmName}
                      type="monotone"
                      dataKey={vmName}
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3, fill: COLORS[index % COLORS.length] }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MinAvailableRam;